import { motion } from 'motion/react';
import Image from 'next/image';
import Link from 'next/link';
import { memo, useMemo } from 'react';
import { ASSET_TYPE } from '@/types/asset';
import { SearchButton } from '@/features/GlobalSearch/components/search/SearchButton';
import { PopularSearchCarousel } from '@/features/GlobalSearch/components/shared/PopularSearchCarousel';
import { SVLCategoryCarousel } from '@/features/GlobalSearch/components/shared/SVLCategoryCarousel';
import { SectionHeading } from '@/features/GlobalSearch/components/trending/SectionHeading';
import { SV_ZONE_LINKS } from '@/features/StockFootage/StockFootageZones/zone-links';
import useTWScreens from '@/hooks/ui/useTWScreens';
import { isStockVideoDocument } from '@/utils/typeGuards';
import type { AssetType } from '@/features/GlobalSearch/types/index';
import type { GenericSearchResult } from '@/features/SearchPage/types';
import type { SearchResponseHit, VideoCategory } from '@music-vine/setlist';
const MAX_POPULAR_SEARCHES = 6;
const MAX_CATEGORIES = 8;
const MAX_ASSETS = 4;

// Format zone links as search hits for PopularSearchCarousel compatibility
const ZONE_HITS = SV_ZONE_LINKS.filter(zone => zone.slug !== 'featured') // Exclude "Featured" as it's just the index page
.slice(0, MAX_POPULAR_SEARCHES).map(zone => ({
  document: {
    id: zone.slug,
    name: zone.name,
    slug: zone.slug
  },
  highlight: {},
  text_match: 100
}));
interface StockVideoTrendingSectionProps {
  closePopup: () => void;
  onNameClick: (content: string | undefined, endSlug: string | undefined, assetType: AssetType | 'general') => void;
  trackTrendingClick: (endSlug: string) => void;
  trendingStockVideoAssets?: {
    hits?: SearchResponseHit<GenericSearchResult>[];
  };
  trendingStockVideoCategories?: {
    hits?: SearchResponseHit<VideoCategory>[];
  };
}
export const StockVideoTrendingSection = memo(({
  trendingStockVideoCategories,
  trendingStockVideoAssets,
  closePopup,
  trackTrendingClick,
  onNameClick
}: StockVideoTrendingSectionProps) => {
  const {
    isMd
  } = useTWScreens();
  const allCategories = useMemo(() => (trendingStockVideoCategories?.hits ?? []).slice(0, MAX_CATEGORIES), [trendingStockVideoCategories?.hits]);
  const assetsData = trendingStockVideoAssets?.hits ?? [];
  const hasZones = ZONE_HITS.length > 0;
  const hasCategories = allCategories.length > 0;
  const hasAssets = assetsData.length > 0;
  const AnimatedContainer = isMd ? motion.div : 'div';
  const AnimatedItem = isMd ? motion.div : 'div';
  return <AnimatedContainer animate={isMd ? {
    opacity: 1
  } : undefined} initial={isMd ? {
    opacity: 0
  } : undefined} style={{
    position: 'relative'
  }} transition={isMd ? {
    duration: 0.15
  } : undefined}>
        {/* Popular searches - Zone categories (hardcoded) */}
        {hasZones && <div className="main-search-results my-4 px-4 md:my-0 md:mb-5 md:px-0">
            <div className="mb-2">
              <SectionHeading>Popular searches</SectionHeading>
            </div>

            {/* Desktop view */}
            <AnimatedItem className="hidden max-h-6 overflow-hidden md:flex md:flex-wrap md:gap-2 focus-within:[&:has(:focus-visible)]:max-h-none focus-within:[&:has(:focus-visible)]:overflow-visible">
              {ZONE_HITS.map((hit, idx) => {
          const {
            document
          } = hit;
          const href = `/stock-video/category/${document.slug}`;
          return <AnimatedItem animate={isMd ? {
            opacity: 1
          } : undefined} initial={isMd ? {
            opacity: 0
          } : undefined} key={document.id} transition={isMd ? {
            duration: 0.15,
            delay: Math.min(idx * 0.02, 0.1)
          } : undefined}>
                    <SearchButton assetType={ASSET_TYPE.STOCK_VIDEO} closePopup={closePopup} href={href} id={document.id} name={document.name.toLowerCase()} onNameClick={onNameClick} testId="popular-stock-video-search" />
                  </AnimatedItem>;
        })}
            </AnimatedItem>

            {/* Mobile Carousel */}
            <div className="-mx-4 block md:hidden">
              <PopularSearchCarousel assetType={ASSET_TYPE.STOCK_VIDEO} closePopup={closePopup} getHref={slug => `/stock-video/category/${slug}`} hits={ZONE_HITS} onNameClick={onNameClick} testId="popular-stock-video-search" />
            </div>
          </div>}

        {/* Categories - All categories */}
        {!!hasCategories && <div className="main-search-results my-4 px-4 md:my-0 md:mb-5 md:px-0">
            <div className="mb-2">
              <SectionHeading>Categories</SectionHeading>
            </div>
            <SVLCategoryCarousel closePopup={closePopup} data={allCategories} onTileClick={trackTrendingClick} />
          </div>}

        {/* Stock video assets */}
        {!!hasAssets && <div className="main-search-results my-4 px-4 md:my-0 md:mb-0 md:px-0">
            <div className="mb-2">
              <SectionHeading>Stock videos</SectionHeading>
            </div>
            <AnimatedItem className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {assetsData.slice(0, MAX_ASSETS).map((hit, idx) => {
          if (!isStockVideoDocument(hit.document)) {
            return null;
          }
          const stockVideo = hit.document;
          const assetUrl = `/stock-video/asset/${stockVideo.slug}`;
          return <AnimatedItem animate={isMd ? {
            opacity: 1
          } : undefined} initial={isMd ? {
            opacity: 0
          } : undefined} key={`sv-asset-${stockVideo.id}`} transition={isMd ? {
            duration: 0.2,
            delay: Math.min(idx * 0.05, 0.25)
          } : undefined}>
                    <Link className="-m-2 flex flex-col gap-1 rounded-xl p-2 transition-all duration-300 hover:bg-gray-100 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-pink-500 dark:hover:bg-gray-950" href={assetUrl} onClick={() => {
              closePopup();
              trackTrendingClick(assetUrl);
            }}>
                      <div className="relative aspect-video w-full overflow-hidden rounded-lg">
                        {stockVideo.thumbnail_url ? <Image alt={stockVideo.title ?? 'Stock video'} className="object-cover" fill sizes="(max-width: 768px) 100vw, 50vw" src={stockVideo.thumbnail_url} /> : <div className="size-full bg-gray-200 dark:bg-gray-800" />}
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col">
                        <h3 className="truncate font-sans font-semibold text-gray-950 text-sm dark:text-white">
                          {stockVideo.title}
                        </h3>
                        {!!stockVideo.contributor_name && <p className="inline-flex text-gray-700 text-xs dark:text-white">
                            {stockVideo.contributor_name}
                          </p>}
                      </div>
                    </Link>
                  </AnimatedItem>;
        })}
            </AnimatedItem>
          </div>}
      </AnimatedContainer>;
});
StockVideoTrendingSection.displayName = 'StockVideoTrendingSection';